import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@akuna/ui";
import { Card, CardContent } from "@/components/ui/card";
import { FileText, Upload, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  useParseXml,
  useImportXmlItems,
  useExistingXmlImport,
  XmlImportItemSelection,
} from "@/hooks/useXmlImport";
import { useFiscalEnabled } from "@/hooks/useFiscalEnabled";
import { useModuleAccess } from "@/modules/useModuleAccess";
import { Alert, AlertDescription, AlertTitle } from "@akuna/ui";
import { XmlItemSelectionTable } from "./XmlItemSelectionTable";
import { Badge } from "@/components/ui/badge";

interface CatalogXmlImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete?: () => void;
}

type ImportStep = "upload" | "select" | "importing" | "complete";

export function CatalogXmlImportDialog({
  open,
  onOpenChange,
  onImportComplete,
}: CatalogXmlImportDialogProps) {
  const [step, setStep] = useState<ImportStep>("upload");
  const [xmlContent, setXmlContent] = useState<string>("");
  const [parsedData, setParsedData] = useState<any>(null);
  const [selectedItems, setSelectedItems] = useState<XmlImportItemSelection[]>([]);

  const { t } = useTranslation("erp");
  const fiscalEnabled = useFiscalEnabled();
  const { has } = useModuleAccess();
  const stockEnabled = has("stock");

  const parseXml = useParseXml();
  const importItems = useImportXmlItems();
  const { data: existingImport, isLoading: checkingDuplicate } = useExistingXmlImport(parsedData?.invoice?.key);
  const alreadyImported = !!existingImport;

  useEffect(() => {
    if (!open) handleReset();
  }, [open]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".xml")) {
      toast.error(t("xmlImportDialog.toast.invalidFile"));
      return;
    }

    try {
      const text = await file.text();
      setXmlContent(text);

      // Parse XML
      const result = await parseXml.mutateAsync(text);
      setParsedData(result);
      setStep("select");
      toast.success(t("xmlImportDialog.toast.parsed", { count: result.items.length }));
    } catch (error: any) {
      toast.error(t("xmlImportDialog.toast.parseError", { message: error.message }));
    }
  };

  const handleSelectionChange = (selections: XmlImportItemSelection[]) => {
    setSelectedItems(selections);
  };

  const handleImport = async () => {
    const itemsToImport = selectedItems.filter((s) => s.action !== "skip");

    if (itemsToImport.length === 0) {
      toast.error(t("xmlImportDialog.toast.selectAtLeastOne"));
      return;
    }

    try {
      setStep("importing");
      await importItems.mutateAsync({
        xmlContent,
        parsedData,
        selectedItems: itemsToImport,
        options: { includeFiscal: fiscalEnabled, includeStock: stockEnabled },
      });
      setStep("complete");
      onImportComplete?.();
    } catch (error: any) {
      toast.error(t("xmlImportDialog.toast.importError", { message: error.message }));
      setStep("select");
    }
  };

  const handleReset = () => {
    setStep("upload");
    setXmlContent("");
    setParsedData(null);
    setSelectedItems([]);
  };

  const handleClose = () => {
    if (step === "importing") return; // Prevent closing during import
    handleReset();
    onOpenChange(false);
  };

  const selectedCount = selectedItems.filter((s) => s.action !== "skip").length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <DialogTitle>{t("xmlImportDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("xmlImportDialog.description")}
            {stockEnabled && ` ${t("xmlImportDialog.stockHint")}`}
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            <Card className="border-2 border-dashed">
              <CardContent className="p-8">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="p-4 rounded-full bg-primary/10">
                    <FileText className="h-12 w-12 text-primary" />
                  </div>
                  <div className="text-center space-y-2">
                    <h3 className="text-lg font-semibold">{t("xmlImportDialog.upload.title")}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t("xmlImportDialog.upload.subtitle")}
                    </p>
                  </div>
                  <div className="relative">
                    <input
                      type="file"
                      id="xml-upload"
                      className="hidden"
                      accept=".xml"
                      onChange={handleFileSelect}
                    />
                    <label htmlFor="xml-upload">
                      <Button asChild variant="outline" className="cursor-pointer">
                        <span>
                          <Upload className="h-4 w-4 mr-2" />
                          {t("xmlImportDialog.upload.button")}
                        </span>
                      </Button>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {parseXml.isPending && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <span className="ml-2 text-muted-foreground">{t("xmlImportDialog.upload.processing")}</span>
              </div>
            )}
          </div>
        )}

        {step === "select" && parsedData && (
          <div className="min-w-0 space-y-4">
            <div>
              <h3 className="text-lg font-semibold">{t("xmlImportDialog.select.title")}</h3>
              <p className="text-sm text-muted-foreground">
                {t("xmlImportDialog.select.supplierInvoice", { supplier: parsedData.supplier.name, invoice: parsedData.invoice.number })}
              </p>
            </div>

            {alreadyImported && (
              <Alert variant="warning">
                <AlertTitle>{t("xmlImportDialog.duplicate.title")}</AlertTitle>
                <AlertDescription>{t("xmlImportDialog.duplicate.description")}</AlertDescription>
              </Alert>
            )}

            <XmlItemSelectionTable
              xmlItems={parsedData.items}
              onSelectionChange={handleSelectionChange}
              showStock={stockEnabled}
              showFiscal={fiscalEnabled}
              alreadyImported={alreadyImported}
            />
          </div>
        )}

        {step === "importing" && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-lg font-medium">{t("xmlImportDialog.importingStep.title")}</p>
            <p className="text-sm text-muted-foreground">
              {t("xmlImportDialog.importingStep.subtitle")}
            </p>
          </div>
        )}

        {step === "complete" && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <div className="p-4 rounded-full bg-green-100 dark:bg-green-900">
              <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">{t("xmlImportDialog.complete.title")}</h3>
              <p className="text-sm text-muted-foreground">
                {t("xmlImportDialog.complete.subtitle", { count: selectedCount })}
              </p>
            </div>
          </div>
        )}

        {step === "select" && parsedData && (
          <DialogFooter>
            <Button variant="outline" onClick={handleReset}>
              {t("xmlImportDialog.select.back")}
            </Button>
            <Button
              onClick={handleImport}
              disabled={selectedCount === 0 || importItems.isPending || checkingDuplicate}
            >
              {importItems.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t("xmlImportDialog.select.importing")}
                </>
              ) : (
                t("xmlImportDialog.select.import", { count: selectedCount })
              )}
            </Button>
          </DialogFooter>
        )}

        {step === "complete" && (
          <DialogFooter>
            <Button onClick={handleClose}>{t("xmlImportDialog.complete.close")}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
